// Conversion attribution panel — surfaces organic SEO landing-page → submission
// attribution captured from the public site (lib/analytics.js).
//
// Powered by GET /api/admin/analytics/conversions?days=<N>. The system of
// record is GA4 — this panel exists for at-a-glance internal review and to
// guarantee visibility even if GA4 sampling / consent drops events.

import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { BarChart3, Users, MapPin, Globe, CheckCircle2, AlertCircle, RefreshCw, Loader2 } from "lucide-react";

const PRIORITY = new Set(["Jabalpur", "Bhopal", "Nagpur"]);

const CATEGORY_LABEL = {
    "near-me": "Near Me pillars",
    "state": "State pages",
    "tier2-city": "Tier-2 / Tier-3 cities",
    "metro-city": "Metro cities",
    "jabalpur": "Jabalpur",
    "bhopal": "Bhopal",
    "nagpur": "Nagpur",
    "1-to-1-home-tuition": "1:1 Home Tuition pages",
    "1-to-1-online-classes": "1:1 Online Classes pages",
    "hub": "India Hub directories",
    "other": "Other",
    "no_attribution": "Legacy / no attribution",
};

export default function ConversionsPanel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/admin/analytics/conversions?days=${days}`);
            setData(data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

    const totals = data?.totals || {};
    const ga = data?.ga4_status || {};

    const maxCat = useMemo(() => Math.max(1, ...(data?.by_page_category || []).map((r) => r.submissions)), [data]);
    const maxCity = useMemo(() => Math.max(1, ...(data?.by_city || []).map((r) => r.submissions)), [data]);
    const maxLanding = useMemo(() => Math.max(1, ...(data?.top_landing_pages || []).map((r) => r.submissions)), [data]);

    return (
        <div data-testid="conversions-panel" className="space-y-6">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-[#1E3A8A]" /> Conversion Insights
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Organic landing-page → submission attribution from public-site CTAs and forms.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="conv-window-select"
                        className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={180}>Last 6 months</option>
                        <option value={365}>Last 12 months</option>
                    </select>
                    <button
                        onClick={load}
                        data-testid="conv-refresh"
                        className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Refresh
                    </button>
                </div>
            </header>

            {/* GA4 validation banner */}
            <div data-testid="ga4-status-banner" className={`rounded-xl border p-4 flex items-start gap-3 ${ga.measurement_id_configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                {ga.measurement_id_configured
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    : <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />}
                <div className="text-sm">
                    <div className="font-semibold text-[#0F172A]">
                        GA4 status: {ga.measurement_id_configured ? `Connected (${ga.measurement_id})` : "Measurement ID not configured"}
                    </div>
                    <div className="text-slate-600 mt-0.5">
                        {ga.measurement_id_configured
                            ? "Public-site events are firing to GA4. Validate in GA4 → Reports → Realtime → Event count by Event name."
                            : "Set Google Analytics 4 Measurement ID under Site Settings → Analytics so live event tracking goes live."}
                    </div>
                    {Array.isArray(ga.events_tracked) && (
                        <div className="text-xs text-slate-500 mt-2">
                            Events tracked: {ga.events_tracked.join(", ")}
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>
            )}

            {/* Totals strip */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KPI label="Total submissions" value={totals.submissions || 0} icon={Users} />
                <KPI label="Inquiries" value={totals.inquiries || 0} />
                <KPI label="Registrations" value={totals.registrations || 0} />
                <KPI label="With attribution" value={totals.with_attribution || 0} />
                <KPI label="Converted" value={`${totals.converted || 0} (${totals.conversion_rate_pct || 0}%)`} />
            </div>

            {/* Priority cities (always visible: Jabalpur, Bhopal, Nagpur) */}
            <section data-testid="priority-cities-block" className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-2xl p-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-2 flex items-center gap-2">
                    <MapPin className="w-3 h-3" /> Highest-priority cities
                </div>
                <h3 className="font-heading text-lg font-bold mb-4">Core 3 — Jabalpur · Bhopal · Nagpur</h3>
                <div className="grid grid-cols-3 gap-4">
                    {(data?.priority_cities || []).map((c) => (
                        <div key={c.city} data-testid={`priority-city-${c.city.toLowerCase()}`} className="bg-white/10 backdrop-blur rounded-xl p-4">
                            <div className="text-3xl font-heading font-bold">{c.submissions}</div>
                            <div className="text-xs uppercase tracking-wider text-blue-200 mt-1">{c.city}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* By page category */}
            <Section title="Submissions by page category" icon={BarChart3} testid="cat-section">
                {(data?.by_page_category || []).length === 0
                    ? <Empty />
                    : (
                        <div className="space-y-2">
                            {data.by_page_category.map((row) => (
                                <Bar
                                    key={row.page_category}
                                    label={CATEGORY_LABEL[row.page_category] || row.page_category}
                                    value={row.submissions}
                                    max={maxCat}
                                    testid={`cat-row-${row.page_category}`}
                                    color={["jabalpur", "bhopal", "nagpur"].includes(row.page_category) ? "amber" : "blue"}
                                />
                            ))}
                        </div>
                    )}
            </Section>

            {/* Top landing pages */}
            <Section title="Top landing pages" icon={Globe} testid="landing-section">
                {(data?.top_landing_pages || []).length === 0
                    ? <Empty />
                    : (
                        <div className="space-y-2">
                            {data.top_landing_pages.map((row) => (
                                <Bar key={row.landing_path} label={row.landing_path} value={row.submissions} max={maxLanding} mono />
                            ))}
                        </div>
                    )}
            </Section>

            {/* By city */}
            <Section title="Submissions by city" icon={MapPin} testid="city-section">
                {(data?.by_city || []).length === 0
                    ? <Empty />
                    : (
                        <div className="space-y-2">
                            {data.by_city.map((row) => (
                                <Bar key={row.city} label={row.city} value={row.submissions} max={maxCity} color={PRIORITY.has(row.city) ? "amber" : "blue"} />
                            ))}
                        </div>
                    )}
            </Section>

            {/* UTM */}
            <div className="grid lg:grid-cols-3 gap-4">
                <UtmList title="By UTM source"   rows={data?.by_utm_source}   k="utm_source" />
                <UtmList title="By UTM medium"   rows={data?.by_utm_medium}   k="utm_medium" />
                <UtmList title="By UTM campaign" rows={data?.by_utm_campaign} k="utm_campaign" />
            </div>
        </div>
    );
}

const KPI = ({ label, value, icon: Icon }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
            {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </div>
        <div className="font-heading text-2xl font-bold text-[#0F172A]">{value}</div>
    </div>
);

const Section = ({ title, icon: Icon, children, testid }) => (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
            {Icon && <Icon className="w-4 h-4 text-[#1E3A8A]" />}
            <h3 className="font-heading font-semibold text-[#0F172A]">{title}</h3>
        </div>
        {children}
    </section>
);

const Empty = () => <p className="text-sm text-slate-500">No submissions in this window yet.</p>;

const Bar = ({ label, value, max, mono = false, color = "blue", testid }) => {
    const pct = max ? (value / max) * 100 : 0;
    const bg = color === "amber" ? "bg-amber-500" : "bg-[#1E3A8A]";
    return (
        <div data-testid={testid} className="flex items-center gap-3">
            <div className={`text-xs ${mono ? "font-mono" : ""} text-slate-700 truncate flex-1`}>{label}</div>
            <div className="w-2/3 relative h-7 bg-slate-100 rounded-md overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${bg} rounded-md transition-all`} style={{ width: `${pct}%` }}></div>
                <div className="absolute inset-0 flex items-center justify-end px-2 text-xs font-semibold text-[#0F172A]">{value}</div>
            </div>
        </div>
    );
};

const UtmList = ({ title, rows, k }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h4 className="font-heading font-semibold text-[#0F172A] text-sm mb-3">{title}</h4>
        {!rows || rows.length === 0
            ? <p className="text-xs text-slate-500">No UTM-tagged traffic in this window.</p>
            : (
                <ul className="space-y-1.5 text-sm">
                    {rows.map((r) => (
                        <li key={r[k]} className="flex items-center justify-between gap-2">
                            <span className="text-slate-700 truncate">{r[k]}</span>
                            <span className="font-semibold text-[#0F172A] shrink-0">{r.submissions}</span>
                        </li>
                    ))}
                </ul>
            )}
    </div>
);
